import { useState } from 'react';

import { Check, Copy, KeyRound } from 'lucide-react';

import { Alert } from '@/components/feedback/Alert';
import { Button } from '@/components/ui/Button';
import { Modal } from '@/components/ui/Modal';

import styles from './InviteLinkModal.module.css';

export type InviteLinkKind = 'invite' | 'reset';

export type InviteLinkModalProps = {
  open: boolean;
  onClose: () => void;
  kind: InviteLinkKind;
  personName: string;
  token: string;
  expiresAt: string;
};

/**
 * The one time a link is shown.
 *
 * There is no email in version 1, so the administrator passes this on themselves.
 * Only its hash is stored, so it genuinely cannot be shown again. The copy says so
 * plainly, and the row menu offers a fresh one as the way out rather than pretending
 * this one can be recovered.
 */
export function InviteLinkModal({
  open,
  onClose,
  kind,
  personName,
  token,
  expiresAt,
}: InviteLinkModalProps) {
  const [copied, setCopied] = useState(false);
  const link = `${window.location.origin}/set-password?token=${token}`;

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(link);
    } catch {
      // Denied outside a secure context. The link is on screen and selectable.
    }
    setCopied(true);
    window.setTimeout(() => {
      setCopied(false);
    }, 2000);
  };

  const expires = new Date(expiresAt).toLocaleString(undefined, {
    dateStyle: 'medium',
    timeStyle: 'short',
  });

  return (
    <Modal
      open={open}
      onClose={onClose}
      size="md"
      icon={<KeyRound size={19} />}
      title={kind === 'invite' ? 'Invitation link' : 'Password reset link'}
      description={
        kind === 'invite'
          ? `Send this to ${personName}. They choose their own password, so nobody else ever knows it.`
          : `Send this to ${personName}. Their current password keeps working until they use it.`
      }
      footer={<Button onClick={onClose}>Done</Button>}
    >
      <Alert tone="warning" className={styles.warning}>
        This is shown once and cannot be retrieved. If you lose it, issue a new one from the row
        menu.
      </Alert>

      <div className={styles.linkRow}>
        <code className={styles.link}>{link}</code>
        <Button
          size="sm"
          variant={copied ? 'ghost' : 'primary'}
          icon={copied ? <Check size={13} /> : <Copy size={13} />}
          onClick={() => {
            void copy();
          }}
        >
          {copied ? 'Copied' : 'Copy'}
        </Button>
      </div>

      <p className={styles.expiry}>Expires {expires}.</p>
    </Modal>
  );
}
